<template>
  <v-app>
    <!-- Navigation Bar -->
    <v-app-bar app color="#FFFFFF">
      <v-container class="d-flex align-center">
        <!-- Mobile Menu Icon -->
        <v-app-bar-nav-icon @click.stop="drawer = !drawer" class="d-md-none"></v-app-bar-nav-icon>

        <!-- Title -->
        <v-img src="@/assets/logo.png" contain class="nav-logo"></v-img>
        <v-spacer />

        <!-- Mobile "Enquire Now" Button (Visible only on mobile) -->
        <v-btn v-if="isMobile" color="primary" variant="outlined" @click="scrollToSection('enquire')" class="mobile-enquire-button">
          Enquire Now
        </v-btn>

        <!-- Desktop Navigation Links (Hidden on small screens) -->
        <div class="d-none d-md-flex navigation-links">
          <v-btn text @click="scrollToSection('about')">Project Info</v-btn>
          <v-btn text @click="scrollToSection('brochure')">E-Brochure</v-btn>
          <v-btn text @click="scrollToSection('location')">Location</v-btn>
          <v-btn text @click="scrollToSection('floorplan')">Floor Plan & Pricing</v-btn>
          <v-btn text @click="scrollToSection('showflat')">Showflat Details</v-btn>
          <v-btn color="primary" variant="outlined" @click="scrollToSection('enquire')">Enquire Now</v-btn>
        </div>
      </v-container>
    </v-app-bar>

    <!-- Mobile Drawer Navigation -->
    <v-navigation-drawer v-model="drawer" temporary app class="d-md-none mobile-drawer">
      <v-list>
        <v-list-item-group>
          <v-list-item v-for="(item, index) in navLinks" :key="index" @click="scrollToSection(item.section)">
            <v-list-item-title class="drawer-item">{{ item.text }}</v-list-item-title>
          </v-list-item>
        </v-list-item-group>
      </v-list>
    </v-navigation-drawer>

    <!-- Main Content -->
    <v-main style="background-color: #E2D9CA;">
      <v-container>
        <section id="home" ref="home" class="section">
          <HomeView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="about" ref="about" class="section">
          <AboutView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="brochure" ref="brochure" class="section">
          <BrochureView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="location" ref="location" class="section">
          <LocationView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="floorplan" ref="floorplan" class="section">
          <FloorplanView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="showflat" ref="showflat" class="section">
          <ShowflatView :scroll-to-section="scrollToSection"/>
        </section>

        <section id="enquire" ref="enquire" class="section">
          <FormView/>
          <DisclaimView/>
        </section>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import HomeView from '@/views/1_HomeView.vue'; 
import AboutView from '@/views/2_AboutView.vue'; 
import BrochureView from '@/views/3_BrochureView.vue';
import LocationView from '@/views/4_LocationView.vue';
import FloorplanView from '@/views/5_FloorplanView.vue';
import ShowflatView from '@/views/6_ShowflatView.vue';
import FormView from '@/views/7_FormView.vue'; 
import DisclaimView from '@/views/8_DisclaimView.vue'; 

export default {
  name: 'App',
  data() {
    return {
      drawer: false, // Controls the mobile menu
      isMobile: window.innerWidth <= 960, // Track screen size
      navLinks: [
        { text: "Project Info", section: "about" },
        { text: "E-Brochure", section: "brochure" },
        { text: "Location", section: "location" },
        { text: "Floor Plan & Pricing", section: "floorplan" },
        { text: "Showflat Details", section: "showflat" },
        { text: "Enquire Now", section: "enquire" }
      ]
    };
  },
  components: {
    HomeView, AboutView, BrochureView, LocationView, FloorplanView, ShowflatView, FormView, DisclaimView
  },
  methods: {
    scrollToSection(sectionId) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
        history.pushState(null, null, `#${sectionId}`);
        this.drawer = false; // Close drawer after clicking a link (on mobile)
      }
    },
    updateScreenSize() {
      this.isMobile = window.innerWidth <= 960;
    }
  },
  mounted() {
    window.addEventListener('resize', this.updateScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScreenSize);
  }
};
</script>

<style>
/* General Styles */
.section {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid #ddd;
}

/* App Bar Styles */
.nav-logo {
  height: 48px; /* Keep smaller than navbar height */
  width: auto; /* Maintain aspect ratio */
}

.app-title {
  font-size: 1.5rem;
  font-weight: bold;
}

.mobile-enquire-button {
  font-size: 0.9rem;
}

.navigation-links {
  gap: 10px; /* Space between navigation buttons */
}

/* Mobile Drawer Styles */
.mobile-drawer {
  background-color: #f5f5f5; /* Light background */
}

.drawer-item {
  font-size: 1rem;
  font-weight: 500;
  color: #2c3e50; /* Dark text color */
  padding: 12px 24px;
}

.drawer-item:hover {
  background-color: #e0e0e0; /* Hover effect */
}

/* Responsive Adjustments */
@media (max-width: 960px) {
  .section {
    padding: 1rem;
  }

  .app-title {
    font-size: 1.2rem; /* Smaller title on mobile */
  }

  .mobile-enquire-button {
    font-size: 0.8rem;
  }

  .navigation-links {
    display: none; /* Hide desktop navigation on mobile */
  }

  .mobile-drawer {
    width: 250px; /* Adjust drawer width */
  }

  .drawer-item {
    font-size: 0.9rem; /* Smaller text in drawer */
  }
}

/* Customize primary and secondary button colors */
.v-btn--primary {
  background-color: #B8AFA6;
}

.v-btn--secondary {
  background-color: #8BB7B1;
  color: white;
}
</style>